import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import logoPng from "../img/logo.png";

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  Home,
  Users,
  Plus,
  LogOut,
  Settings,
  Globe,
} from 'lucide-react';

import { auth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const language = (i18n.language || 'en').slice(0, 2);
  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(next);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {}
    navigate('/auth');
  };

  const isActive = (path) => location.pathname === path;

  const displayName =
    user?.displayName ||
    user?.email?.split('@')[0] ||
    '';

  const initials = React.useMemo(() => {
    if (user?.displayName) {
      const parts = user.displayName.trim().split(' ');
      const first = parts[0]?.[0] || '';
      const second = parts[1]?.[0] || '';
      return (first + second || first).toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  }, [user]);

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <img
                src={logoPng}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>

            <span className="text-xl font-bold text-slate-900">
              ZiiHUB
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button
              variant={isActive('/dashboard') ? 'default' : 'ghost'}
              onClick={() => navigate('/dashboard')}
              data-testid="nav-dashboard"
            >
              <Home size={16} className="mr-2" />
              {t('nav.dashboard')}
            </Button>

            <Button
              variant={isActive('/groups') ? 'default' : 'ghost'}
              onClick={() => navigate('/groups')}
              data-testid="nav-groups"
            >
              <Users size={16} className="mr-2" />
              {t('nav.browse')}
            </Button>

            <Button
              variant={isActive('/groups/create') ? 'default' : 'ghost'}
              onClick={() => navigate('/groups/create')}
              data-testid="nav-create-group"
            >
              <Plus size={16} className="mr-2" />
              {t('nav.createGroup')}
            </Button>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-3">
            {/* Language Switcher */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center space-x-1"
            >
              <Globe size={16} />
              <span className="font-semibold">
                {language.toUpperCase()}
              </span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-600 to-orange-400 rounded-full flex items-center justify-center text-white font-semibold">
                    {initials}
                  </div>
                  <span className="hidden md:inline">
                    {displayName}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-semibold">{displayName}</p>
                    <p className="text-xs text-slate-500 font-normal">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard')}
                  data-testid="menu-dashboard"
                >
                  <Home size={16} className="mr-2" />
                  {t('nav.dashboard')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/groups')}
                  data-testid="menu-browse"
                >
                  <Users size={16} className="mr-2" />
                  {t('nav.browseGroups')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  data-testid="menu-settings"
                >
                  <Settings size={16} className="mr-2" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600"
                  data-testid="menu-logout"
                >
                  <LogOut size={16} className="mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex border-t border-slate-200 py-2 space-x-1">
          <Button
            size="sm"
            variant={isActive('/dashboard') ? 'default' : 'ghost'}
            onClick={() => navigate('/dashboard')}
            className="flex-1"
          >
            <Home size={16} />
          </Button>
          <Button
            size="sm"
            variant={isActive('/groups') ? 'default' : 'ghost'}
            onClick={() => navigate('/groups')}
            className="flex-1"
          >
            <Users size={16} />
          </Button>
          <Button
            size="sm"
            variant={isActive('/groups/create') ? 'default' : 'ghost'}
            onClick={() => navigate('/groups/create')}
            className="flex-1"
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
